import React, { useMemo } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import { Divider } from 'components/shared/Divider';

interface SchoolsProps {
  disclaimer?: string | null;
  schools?: Array<{
    id?: string | null;
    name?: string | null;
    rating?: number | null;
  }> | null;
  schoolDistricts?: string[] | null;
  summary?: string | null;
}

export const Schools: React.FC<SchoolsProps> = ({
  disclaimer,
  schools,
  schoolDistricts,
  summary,
}) => {
  const elementarySchoolDistrict = schoolDistricts?.filter(
    (district) => !district.toLowerCase().includes('high'),
  )[0];
  const highSchoolDistrict =
    schoolDistricts?.filter((district) => district.toLowerCase().includes('high'))[0] ??
    elementarySchoolDistrict;
  const description = useMemo(
    () => (
      <Col>
        {summary && <Text className="pb-6 text-body-sm text-od-content-primary">{summary}</Text>}
        <Col>
          {!!schoolDistricts?.length && (
            <Text as="span" className="py-3 text-label-sm text-od-content-secondary">
              School districts
            </Text>
          )}
          {elementarySchoolDistrict && (
            <>
              <Row className="justify-between">
                <Text className="text-body-sm text-od-content-primary">Elementary</Text>
                <Text className="text-label-sm text-od-content-primary">
                  {elementarySchoolDistrict}
                </Text>
              </Row>
              <Divider className="py-4" />
            </>
          )}
          {highSchoolDistrict && (
            <>
              <Row className="justify-between">
                <Text className="text-body-sm text-od-content-primary">High School</Text>
                <Text className="text-label-sm text-od-content-primary">{highSchoolDistrict}</Text>
              </Row>
              <Divider className="py-4" />
            </>
          )}
          {schools?.length && (
            <Text as="span" className="py-3 text-label-sm text-od-content-secondary">
              Schools
            </Text>
          )}
          {schools?.map((school, index) => (
            <Col key={school.id}>
              <Row className={cn('justify-between', index === schools.length - 1 && 'pb-6')}>
                <Text className="text-body-sm capitalize text-od-content-primary">
                  {school?.name?.toLowerCase()}
                </Text>
                <Text className="text-label-sm text-od-content-primary">
                  {school?.rating ? `${school.rating} / 10` : 'N/A'}
                </Text>
              </Row>
              {index !== schools.length - 1 && <Divider className="py-4" />}
            </Col>
          ))}
        </Col>
        {disclaimer && (
          <Col>
            <Text className="text-label-sm text-od-content-inactive-primary">{disclaimer}</Text>
          </Col>
        )}
      </Col>
    ),
    [],
  );

  return (
    <Collapsible.Root defaultOpen className="w-full">
      <Collapsible.Trigger className="group flex w-full items-center justify-between gap-2 py-6">
        <Text
          as="span"
          className="text-left text-label-xl text-od-content-primary lg:text-subheader-xs"
        >
          Schools
        </Text>
        <Box className="shrink-0 text-od-content-primary transition-transform duration-200 group-data-[panel-open]:rotate-180">
          <IconChevronDown size={20} />
        </Box>
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-4">{description}</Collapsible.Panel>
    </Collapsible.Root>
  );
};
